"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";

import {
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    Minus,
    Plus,
    ShoppingCart,
} from "lucide-react";

import { apiurl } from "@/app/common/apiurl";
import { useCart } from "@/app/context/web/CartContext";
import CircleLoader from "@/app/common/CircleLoader";

// ==========================================================
// PRODUCT DETAIL PAGE
// ==========================================================

export default function ProductDetail() {
    // ========================================================
    // PRODUCT STATES
    // ========================================================
    const { addcartdata, increasequantity, decreasequantity } = useCart()
    const [product, setProduct] = useState([]);

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [imgurl, setImgurl] = useState("");

    const [productImages, setProductImages] = useState([]);
    const [selectedImage, setSelectedImage] = useState("");

    const [quantity, setQuantity] = useState(1);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // ========================================================
    // CURRENCY STATES
    // ========================================================

    const RATES_API =
        "https://api.frankfurter.dev/v2/rates";

    const [selectedCurrency, setSelectedCurrency] =
        useState("RUB");

    const [rates, setRates] = useState({});

    const [ratesLoading, setRatesLoading] =
        useState(true);

    const [ratesError, setRatesError] =
        useState("");

    // ========================================================
    // GET CATEGORY NAME
    // ========================================================

    const getCategoryName = (categoryId) => {
        if (!categoryId) {
            return "-";
        }

        const category = categories.find(
            (item) =>
                String(item?._id) ===
                String(categoryId),
        );

        if (!category) {
            return categoryId;
        }

        return (
            category.Category_Name ||
            category.category_name ||
            category.Name ||
            category.name ||
            categoryId
        );
    };

    // ========================================================
    // GET SUB CATEGORY NAME
    // ========================================================

    const getSubCategoryName = (subCategoryId) => {
        if (!subCategoryId) {
            return "-";
        }

        const subCategory = subcategories.find(
            (item) =>
                String(item?._id) ===
                String(subCategoryId),
        );

        if (!subCategory) {
            return subCategoryId;
        }

        return (
            subCategory.Sub_Category_Name ||
            subCategory.SubCategory_Name ||
            subCategory.Sub_Category ||
            subCategory.category_name ||
            subCategory.Name ||
            subCategory.name ||
            subCategoryId
        );
    };

    // ========================================================
    // GET PRODUCT IMAGES
    // ========================================================

    const getProductImages = (
        productData,
        imageUrl,
    ) => {
        if (!productData) {
            return [];
        }

        const images = Array.isArray(
            productData?.Product_Images,
        )
            ? productData.Product_Images
            : [];

        const finalImages = [];

        images.forEach((image) => {
            if (!image) {
                return;
            }

            const imageName =
                image?.originalname ||
                image?.filename ||
                "";

            if (!imageName) {
                return;
            }

            const cleanImageName =
                String(imageName).trim();

            // ==================================================
            // IMAGE IS ALREADY A FULL URL
            // ==================================================

            if (
                /^https?:\/\//i.test(
                    cleanImageName,
                )
            ) {
                finalImages.push(
                    cleanImageName,
                );

                return;
            }

            // ==================================================
            // BUILD IMAGE URL
            // ==================================================

            if (imageUrl) {
                const finalImageUrl =
                    `${imageUrl.replace(
                        /\/$/,
                        "",
                    )}/${cleanImageName.replace(
                        /^\//,
                        "",
                    )}`;

                finalImages.push(
                    finalImageUrl,
                );
            }
        });

        return finalImages;
    };

    // ========================================================
    // FETCH CURRENCY RATES
    // ========================================================

    const fetchRates = async () => {
        try {
            setRatesLoading(true);
            setRatesError("");

            const response = await fetch(
                RATES_API,
                {
                    method: "GET",
                    cache: "no-store",
                },
            );

            if (!response.ok) {
                throw new Error(
                    "Unable to fetch currency rates.",
                );
            }

            const data =
                await response.json();

            if (!Array.isArray(data)) {
                throw new Error(
                    "Invalid currency rates response.",
                );
            }

            const rateMap = {};

            data.forEach((item) => {
                if (
                    item?.quote &&
                    typeof item?.rate ===
                    "number"
                ) {
                    rateMap[item.quote] =
                        item.rate;
                }
            });

            setRates(rateMap);
        } catch (error) {
            console.error(
                "CURRENCY RATE ERROR:",
                error,
            );

            setRates({});

            setRatesError(
                "Currency rates are currently unavailable.",
            );
        } finally {
            setRatesLoading(false);
        }
    };

    // ========================================================
    // AVAILABLE CURRENCIES
    // ========================================================

    const availableCurrencies = useMemo(() => {
        const excludedCurrencies = [
            "XAU",
            "XAG",
            "XPD",
            "XPT",
            "XDR",
        ];

        const currencyList =
            Object.keys(rates).filter(
                (currency) =>
                    !excludedCurrencies.includes(
                        currency,
                    ),
            );

        // RUB is the database/base currency
        if (
            !currencyList.includes("RUB")
        ) {
            currencyList.unshift(
                "RUB",
            );
        }

        return currencyList.sort(
            (a, b) => {
                if (a === "RUB") {
                    return -1;
                }

                if (b === "RUB") {
                    return 1;
                }

                return a.localeCompare(b);
            },
        );
    }, [rates]);

    // ========================================================
    // GET RUB RATE FROM FRANKFURTER
    // ========================================================

    const getINRRate = () => {
        const inrRate = Number(
            rates?.RUB,
        );

        if (
            !Number.isFinite(inrRate) ||
            inrRate <= 0
        ) {
            return null;
        }

        return inrRate;
    };

    // ========================================================
    // CONVERT RUB TO SELECTED CURRENCY
    // ========================================================

    const convertFromINR = (amount) => {
        const numericAmount =
            Number(amount) || 0;

        // ==================================================
        // RUB DOES NOT NEED CONVERSION
        // ==================================================

        if (
            selectedCurrency === "RUB"
        ) {
            return numericAmount;
        }

        const inrRate =
            getINRRate();

        const targetRate = Number(
            rates?.[selectedCurrency],
        );

        if (
            !inrRate ||
            !Number.isFinite(
                targetRate,
            ) ||
            targetRate <= 0
        ) {
            return numericAmount;
        }

        // ==================================================
        // FRANKFURTER RATES ARE EUR BASED
        // ==================================================

        const amountInEUR =
            numericAmount /
            inrRate;

        const convertedAmount =
            amountInEUR *
            targetRate;

        return convertedAmount;
    };

    // ========================================================
    // FORMAT PRICE
    // ========================================================

    const formatPrice = (price) => {
        const convertedPrice =
            convertFromINR(price);

        try {
            return new Intl.NumberFormat(
                "en-US",
                {
                    style: "currency",
                    currency:
                        selectedCurrency,
                    maximumFractionDigits: 2,
                },
            ).format(
                convertedPrice,
            );
        } catch (error) {
            return `${selectedCurrency} ${convertedPrice.toFixed(
                2,
            )}`;
        }
    };

    // ========================================================
    // FETCH PRODUCT DATA
    // ========================================================

    const viewdata = async () => {
        try {
            setLoading(true);
            setError("");

            // ==================================================
            // GET PRODUCT ID FROM URL
            // ==================================================

            const urlParams =
                new URLSearchParams(
                    window.location.search,
                );

            const productId =
                urlParams.get("_id");


            // ==================================================
            // CHECK PRODUCT ID
            // ==================================================

            if (!productId) {
                setError(
                    "Product ID is missing from URL.",
                );

                return;
            }

            // ==================================================
            // FETCH PRODUCT + CATEGORY + SUB CATEGORY
            // ==================================================

            const [
                productResponse,
                categoryResponse,
                subCategoryResponse,
            ] = await Promise.all([
                apiurl.get(
                    "/web/view-products",
                ),

                apiurl.get(
                    "/web/view-categories",
                ),

                apiurl.get(
                    "/web/view-sub-categories",
                ),
            ]);

            // ==================================================
            // PRODUCT DATA
            // ==================================================

            const productData =
                productResponse?.data?.Data
                    ?.viewimages || [];

            // ==================================================
            // IMAGE URL
            // ==================================================

            const imageUrl =
                productResponse?.data?.Data
                    ?.imageurl || "";

            setImgurl(imageUrl);

            // ==================================================
            // MAKE SURE PRODUCT DATA IS ARRAY
            // ==================================================

            const productList =
                Array.isArray(
                    productData,
                )
                    ? productData
                    : [];

            // ==================================================
            // FIND PRODUCT
            // ==================================================

            const selectedProduct =
                productList.find(
                    (item) =>
                        String(
                            item?._id,
                        ) ===
                        String(
                            productId,
                        ),
                );
            // ==================================================
            // PRODUCT NOT FOUND
            // ==================================================

            if (!selectedProduct) {
                setProduct(null);

                setError(
                    "Product not found.",
                );

                return;
            }

            // ==================================================
            // SET PRODUCT
            // ==================================================

            setProduct(
                selectedProduct,
            );

            // ==================================================
            // SET INITIAL QUANTITY TO MOQ
            // ==================================================

            const minimumOrderQuantity =
                Math.max(
                    Number(
                        selectedProduct?.Product_MOQ,
                    ) || 1,
                    1,
                );

            setQuantity(
                minimumOrderQuantity,
            );

            // ==================================================
            // PRODUCT IMAGES
            // ==================================================

            const images =
                getProductImages(
                    selectedProduct,
                    imageUrl,
                );

            setProductImages(
                images,
            );

            // ==================================================
            // FIRST IMAGE
            // ==================================================

            if (images.length > 0) {
                setSelectedImage(
                    images[0],
                );
            }

            // ==================================================
            // CATEGORIES
            // ==================================================

            let categoryData =
                categoryResponse?.data?.Data
                    ?.viewimages ||
                categoryResponse?.data?.Data
                    ?.categories ||
                categoryResponse?.data
                    ?.Data ||
                [];

            if (
                !Array.isArray(
                    categoryData,
                )
            ) {
                categoryData = [];
            }

            setCategories(
                categoryData,
            );

            // ==================================================
            // SUB CATEGORIES
            // ==================================================

            let subCategoryData =
                subCategoryResponse?.data
                    ?.Data?.viewimages ||
                subCategoryResponse?.data
                    ?.Data?.subcategories ||
                subCategoryResponse?.data
                    ?.Data ||
                [];

            if (
                !Array.isArray(
                    subCategoryData,
                )
            ) {
                subCategoryData = [];
            }

            setSubcategories(
                subCategoryData,
            );
        } catch (error) {
            console.error(
                "PRODUCT DETAIL ERROR:",
                error,
            );

            setProduct(null);

            setProductImages([]);

            setSelectedImage("");

            setError(
                "Something went wrong while loading product.",
            );
        } finally {
            setLoading(false);
        }
    };

    // ========================================================
    // LOAD DATA
    // ========================================================

    useEffect(() => {
        viewdata();

        fetchRates();
    }, []);

    // ========================================================
    // PRODUCT MOQ
    // ========================================================

    const productMOQ = Math.max(
        Number(
            product?.Product_MOQ,
        ) || 1,
        1,
    );

    // ========================================================
    // INCREASE QUANTITY
    // EVERY CLICK = +1
    // ========================================================

    const increaseQuantity = () => {
        setQuantity(
            (previousQuantity) =>
                previousQuantity + 1,
        );
    };

    // ========================================================
    // DECREASE QUANTITY
    // EVERY CLICK = -1
    // BUT CANNOT GO BELOW MOQ
    // ========================================================

    const decreaseQuantity = () => {
        setQuantity(
            (previousQuantity) => {
                const newQuantity =
                    previousQuantity - 1;

                if (
                    newQuantity <
                    productMOQ
                ) {
                    return productMOQ;
                }

                return newQuantity;
            },
        );
    };

    // ========================================================
    // ADD TO CART
    // ========================================================

    const addToCart = () => {
        if (!product) {
            return;
        }

        try {
            // ==================================================
            // GET EXISTING CART
            // ==================================================

            const existingCart =
                JSON.parse(
                    localStorage.getItem(
                        "cart",
                    ) || "[]",
                );

            // ==================================================
            // PRODUCT ID
            // ==================================================

            const productId =
                product?._id;

            // ==================================================
            // CHECK EXISTING PRODUCT
            // ==================================================

            const existingProductIndex =
                existingCart.findIndex(
                    (item) =>
                        String(
                            item?.productId,
                        ) ===
                        String(
                            productId,
                        ),
                );

            // ==================================================
            // UPDATE EXISTING PRODUCT
            // ==================================================

            if (
                existingProductIndex !==
                -1
            ) {
                existingCart[
                    existingProductIndex
                ].quantity +=
                    quantity;
            }

            // ==================================================
            // ADD NEW PRODUCT
            // ==================================================

            else {
                existingCart.push({
                    productId:
                        product?._id,

                    Product_Name:
                        product?.Product_Name,

                    Product_Sku:
                        product?.Product_Sku,

                    Product_Category:
                        product?.Product_Category,

                    Product_Sub_Category:
                        product?.Product_Sub_Category,

                    Product_Material:
                        product?.Product_Material,

                    Product_GSM:
                        product?.Product_GSM,

                    Product_Width:
                        product?.Product_Width,

                    Product_Color:
                        product?.Product_Color,

                    Product_Pattern:
                        product?.Product_Pattern,

                    Product_Price:
                        product?.Product_Price,

                    Product_Price_Unit:
                        product?.Product_Price_Unit,

                    Product_MOQ:
                        product?.Product_MOQ,

                    Stock_Quantity:
                        product?.Stock_Quantity,

                    Stock_Unit:
                        product?.Stock_Unit,

                    Product_Description:
                        product?.Product_Description,

                    Product_Images:
                        product?.Product_Images,

                    quantity:
                        quantity,
                });
            }

            // ==================================================
            // SAVE CART
            // ==================================================

            localStorage.setItem(
                "cart",
                JSON.stringify(
                    existingCart,
                ),
            );

            // ==================================================
            // CART UPDATE EVENT
            // ==================================================

            window.dispatchEvent(
                new Event(
                    "cart-updated",
                ),
            );

            alert(
                "Product added to cart.",
            );
        } catch (error) {
            console.error(
                "ADD TO CART ERROR:",
                error,
            );
        }
    };

    // ========================================================
    // LOADING
    // ========================================================

    if (loading) {
        return <CircleLoader />;
    }

    // ========================================================
    // ERROR
    // ========================================================

    if (
        error ||
        !product
    ) {
        return (
            <section className="flex min-h-screen items-center justify-center bg-[#F7F7F7] px-5">

                <div className="w-full max-w-xl rounded-[30px] bg-white p-10 text-center shadow-lg">

                    <h1 className="text-3xl font-semibold text-[#0A2342]">
                        Product Not Found
                    </h1>

                    <p className="mt-4 text-gray-600">
                        {error ||
                            "The requested product could not be found."}
                    </p>

                    <Link
                        href="/products"
                        className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0A2342] px-7 py-3 font-medium text-white transition hover:bg-[#D4AF37] hover:text-[#0A2342]"
                    >
                        <ArrowLeft
                            size={18}
                        />

                        Back to Products
                    </Link>

                </div>

            </section>
        );
    }

    // ========================================================
    // PRODUCT VALUES
    // ========================================================

    const categoryName =
        getCategoryName(
            product?.Product_Category,
        );

    const subCategoryName =
        getSubCategoryName(
            product?.Product_Sub_Category,
        );

    // ========================================================
    // PRODUCT PRICE IN RUB
    // ========================================================

    const productPrice =
        Number(
            product?.Product_Price,
        ) || 0;

    // ========================================================
    // TOTAL PRICE IN RUB
    // ========================================================

    const totalPrice =
        productPrice *
        quantity;

    // ========================================================
    // MAIN UI
    // ========================================================

    return (
        <main className="bg-[#F7F7F7]">

            {/* ==================================================
                BREADCRUMB
            ================================================== */}

            <section className="border-b border-gray-200 bg-white">

                <div className="mx-auto max-w-7xl px-5 py-5">

                    <div className="flex flex-wrap items-center gap-2 text-sm">

                        <Link
                            href="/products"
                            className="text-gray-500 transition hover:text-[#0A2342]"
                        >
                            Products
                        </Link>

                        <span className="text-gray-400">
                            /
                        </span>

                        <span className="font-medium text-[#0A2342]">
                            {product?.Product_Name}
                        </span>

                    </div>

                </div>

            </section>

            {/* ==================================================
                PRODUCT DETAIL
            ================================================== */}

            <section className="px-5 py-12 md:py-20">

                <div className="mx-auto max-w-7xl">

                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">

                        {/* ==================================================
                            LEFT SIDE
                        ================================================== */}

                        <div>

                            {/* ==================================================
                                MAIN IMAGE
                            ================================================== */}

                            <div className="relative overflow-hidden rounded-[35px] bg-white shadow-sm">

                                <div className="relative aspect-square w-full">

                                    {selectedImage ? (
                                        <Image
                                            src={
                                                selectedImage
                                            }
                                            alt={
                                                product?.Product_Name ||
                                                "Product Image"
                                            }
                                            fill
                                            priority
                                            className="object-cover"
                                            sizes="(max-width: 1024px) 100vw, 50vw"
                                        />
                                    ) : (
                                        <div className="flex h-full items-center justify-center text-gray-400">
                                            No Image Available
                                        </div>
                                    )}

                                </div>


                                {/* ==================================================
                                    IMAGE NAVIGATION
                                ================================================== */}

                                {productImages.length >
                                    1 && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const currentIndex =
                                                        productImages.indexOf(
                                                            selectedImage,
                                                        );

                                                    const previousIndex =
                                                        currentIndex <=
                                                            0
                                                            ? productImages.length -
                                                            1
                                                            : currentIndex -
                                                            1;

                                                    setSelectedImage(
                                                        productImages[
                                                        previousIndex
                                                        ],
                                                    );
                                                }}
                                                className="absolute left-5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-[#0A2342] shadow-md transition hover:bg-[#D4AF37]"
                                            >
                                                <ChevronLeft
                                                    size={20}
                                                />
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const currentIndex =
                                                        productImages.indexOf(
                                                            selectedImage,
                                                        );

                                                    const nextIndex =
                                                        currentIndex >=
                                                            productImages.length -
                                                            1
                                                            ? 0
                                                            : currentIndex +
                                                            1;

                                                    setSelectedImage(
                                                        productImages[
                                                        nextIndex
                                                        ],
                                                    );
                                                }}
                                                className="absolute right-5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-[#0A2342] shadow-md transition hover:bg-[#D4AF37]"
                                            >
                                                <ChevronRight
                                                    size={20}
                                                />
                                            </button>
                                        </>
                                    )}

                            </div>

                            {/* ==================================================
                                IMAGE THUMBNAILS
                            ================================================== */}

                            {productImages.length >
                                1 && (
                                    <div className="mt-5 grid grid-cols-4 gap-4 sm:grid-cols-5">

                                        {productImages.map(
                                            (
                                                image,
                                                index,
                                            ) => (
                                                <button
                                                    key={`${image}-${index}`}
                                                    type="button"
                                                    onClick={() =>
                                                        setSelectedImage(
                                                            image,
                                                        )
                                                    }
                                                    className={`relative aspect-square overflow-hidden rounded-2xl border-2 bg-white transition ${selectedImage ===
                                                        image
                                                        ? "border-[#D4AF37]"
                                                        : "border-transparent"
                                                        }`}
                                                >
                                                    <Image
                                                        src={
                                                            image
                                                        }
                                                        alt={`${product?.Product_Name || "Product"} ${index +
                                                            1
                                                            }`}
                                                        fill
                                                        className="object-cover"
                                                        sizes="120px"
                                                    />
                                                </button>
                                            ),
                                        )}

                                    </div>
                                )}

                        </div>

                        {/* ==================================================
                            RIGHT SIDE
                        ================================================== */}

                        <div className="flex flex-col justify-center">

                            {/* ==================================================
                                CATEGORY
                            ================================================== */}

                            <div className="flex flex-wrap items-center gap-3">

                                <span className="rounded-full bg-[#0A2342] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white">
                                    {categoryName}
                                </span>

                                {subCategoryName !==
                                    "-" && (
                                        <span className="rounded-full bg-[#D4AF37]/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#0A2342]">
                                            {
                                                subCategoryName
                                            }
                                        </span>
                                    )}

                            </div>

                            {/* ==================================================
                                PRODUCT NAME
                            ================================================== */}

                            <h1 className="mt-6 text-4xl font-semibold leading-tight text-[#0A2342] md:text-5xl">
                                {
                                    product?.Product_Name
                                }
                            </h1>

                            {/* ==================================================
                                SKU
                            ================================================== */}

                            <div className="mt-4">

                                <span className="text-sm text-gray-500">
                                    SKU:
                                </span>

                                <span className="ml-2 text-sm font-semibold text-[#0A2342]">
                                    {
                                        product?.Product_Sku ||
                                        "-"
                                    }
                                </span>

                            </div>

                            {/* ==================================================
                                PRICE + CURRENCY
                            ================================================== */}

                            <div className="mt-6">

                                <div className="flex flex-wrap items-center justify-between gap-4">

                                    <div className="flex flex-wrap items-end gap-3">

                                        <span className="text-3xl font-bold text-[#0A2342]">
                                            {formatPrice(
                                                productPrice,
                                            )}
                                        </span>

                                        <span className="pb-1 text-base text-gray-500">
                                            /
                                            {" "}
                                            {
                                                product?.Product_Price_Unit ||
                                                "-"
                                            }
                                        </span>

                                    </div>

                                    {/* CURRENCY SELECTOR */}

                                    <div className="flex flex-col gap-1">

                                        <label className="text-xs font-medium uppercase tracking-wide text-gray-400">
                                            Currency
                                        </label>

                                        <select
                                            value={
                                                selectedCurrency
                                            }
                                            onChange={(
                                                event,
                                            ) =>
                                                setSelectedCurrency(
                                                    event
                                                        .target
                                                        .value,
                                                )
                                            }
                                            disabled={
                                                ratesLoading
                                            }
                                            className="min-w-32.5 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-[#0A2342] outline-none transition focus:border-[#D4AF37] disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {availableCurrencies.map(
                                                (
                                                    currency,
                                                ) => (
                                                    <option
                                                        key={
                                                            currency
                                                        }
                                                        value={
                                                            currency
                                                        }
                                                    >
                                                        {
                                                            currency
                                                        }
                                                    </option>
                                                ),
                                            )}

                                            {!availableCurrencies.includes(
                                                "RUB",
                                            ) && (
                                                    <option value="RUB">
                                                        RUB
                                                    </option>
                                                )}
                                        </select>

                                    </div>

                                </div>

                                {/* RATE STATUS */}

                                <div className="mt-2">

                                    {ratesLoading && (
                                        <p className="text-xs text-gray-400">
                                            Updating currency rates...
                                        </p>
                                    )}

                                    {!ratesLoading &&
                                        ratesError && (
                                            <p className="text-xs text-red-500">
                                                {
                                                    ratesError
                                                }
                                            </p>
                                        )}

                                    {!ratesLoading &&
                                        !ratesError &&
                                        selectedCurrency !==
                                        "RUB" &&
                                        !getINRRate() && (
                                            <p className="text-xs text-orange-500">
                                                Conversion rate unavailable. Showing RUB value.
                                            </p>
                                        )}

                                </div>

                            </div>

                            {/* ==================================================
                                DESCRIPTION
                            ================================================== */}

                            <div className="mt-8 border-t border-gray-200 pt-8">

                                <h2 className="text-lg font-semibold text-[#0A2342]">
                                    Product Description
                                </h2>

                                <p className="mt-4 text-base leading-8 text-gray-600">
                                    {
                                        product?.Product_Description ||
                                        "No product description available."
                                    }
                                </p>

                            </div>

                            {/* ==================================================
                                PRODUCT SPECIFICATIONS
                            ================================================== */}

                            <div className="mt-8 border-t border-gray-200 pt-8">

                                <h2 className="text-lg font-semibold text-[#0A2342]">
                                    Product Specifications
                                </h2>

                                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">

                                    {/* MATERIAL */}

                                    <div className="rounded-2xl bg-white p-4">

                                        <p className="text-xs uppercase tracking-wider text-gray-400">
                                            Material
                                        </p>

                                        <p className="mt-1 font-semibold text-[#0A2342]">
                                            {
                                                product?.Product_Material ||
                                                "-"
                                            }
                                        </p>

                                    </div>

                                    {/* GSM */}

                                    <div className="rounded-2xl bg-white p-4">

                                        <p className="text-xs uppercase tracking-wider text-gray-400">
                                            GSM
                                        </p>

                                        <p className="mt-1 font-semibold text-[#0A2342]">
                                            {
                                                product?.Product_GSM ??
                                                "-"
                                            }
                                        </p>

                                    </div>

                                    {/* WIDTH */}

                                    <div className="rounded-2xl bg-white p-4">

                                        <p className="text-xs uppercase tracking-wider text-gray-400">
                                            Width
                                        </p>

                                        <p className="mt-1 font-semibold text-[#0A2342]">
                                            {
                                                product?.Product_Width ||
                                                "-"
                                            }
                                        </p>

                                    </div>

                                    {/* COLOR */}

                                    <div className="rounded-2xl bg-white p-4">

                                        <p className="text-xs uppercase tracking-wider text-gray-400">
                                            Color
                                        </p>

                                        <p className="mt-1 font-semibold text-[#0A2342]">
                                            {
                                                product?.Product_Color ||
                                                "-"
                                            }
                                        </p>

                                    </div>

                                    {/* PATTERN */}

                                    <div className="rounded-2xl bg-white p-4">

                                        <p className="text-xs uppercase tracking-wider text-gray-400">
                                            Pattern
                                        </p>

                                        <p className="mt-1 font-semibold text-[#0A2342]">
                                            {
                                                product?.Product_Pattern ||
                                                "-"
                                            }
                                        </p>

                                    </div>

                                    {/* CATEGORY */}

                                    <div className="rounded-2xl bg-white p-4">

                                        <p className="text-xs uppercase tracking-wider text-gray-400">
                                            Category
                                        </p>

                                        <p className="mt-1 font-semibold text-[#0A2342]">
                                            {categoryName}
                                        </p>

                                    </div>

                                    {/* SUB CATEGORY */}

                                    <div className="rounded-2xl bg-white p-4">

                                        <p className="text-xs uppercase tracking-wider text-gray-400">
                                            Sub Category
                                        </p>

                                        <p className="mt-1 font-semibold text-[#0A2342]">
                                            {
                                                subCategoryName
                                            }
                                        </p>

                                    </div>

                                    {/* MOQ */}

                                    <div className="rounded-2xl bg-white p-4">

                                        <p className="text-xs uppercase tracking-wider text-gray-400">
                                            Minimum Order
                                        </p>

                                        <p className="mt-1 font-semibold text-[#0A2342]">
                                            {
                                                product?.Product_MOQ ??
                                                "-"
                                            }

                                            {" "}

                                            {
                                                product?.Product_Price_Unit ||
                                                "-"
                                            }
                                        </p>

                                    </div>

                                    {/* STOCK */}

                                    <div className="rounded-2xl bg-white p-4">

                                        <p className="text-xs uppercase tracking-wider text-gray-400">
                                            Stock
                                        </p>

                                        <p className="mt-1 font-semibold text-[#0A2342]">
                                            {
                                                product?.Stock_Quantity ??
                                                "-"
                                            }

                                            {" "}

                                            {
                                                product?.Stock_Unit ||
                                                ""
                                            }
                                        </p>

                                    </div>

                                </div>

                            </div>

                            {/* ==================================================
                                QUANTITY
                            ================================================== */}

                            <div className="mt-8">

                                <p className="mb-3 text-sm font-semibold text-[#0A2342]">
                                    Quantity
                                    {" "}
                                    (
                                    {
                                        product?.Product_Price_Unit ||
                                        "Unit"
                                    }
                                    )
                                </p>

                                <div className="flex w-fit items-center overflow-hidden rounded-full border border-gray-300 bg-white">

                                    {/* MINUS */}

                                    <button
                                        type="button"
                                        onClick={() => {
                                            decreaseQuantity();
                                            decreasequantity(product?._id || 0);
                                        }}
                                        className="flex h-12 w-12 items-center justify-center text-[#0A2342] transition hover:bg-[#F7F7F7]"
                                    >
                                        <Minus
                                            size={18}
                                        />
                                    </button>

                                    {/* QUANTITY */}

                                    <span className="flex h-12 min-w-20 items-center justify-center border-x border-gray-200 px-4 font-semibold text-[#0A2342]">
                                        {quantity}
                                    </span>

                                    {/* PLUS */}

                                    <button
                                        type="button"
                                        onClick={() => {
                                            increaseQuantity();
                                            increasequantity(product?._id || 0);
                                        }}
                                        className="flex h-12 w-12 items-center justify-center text-[#0A2342] transition hover:bg-[#F7F7F7]"
                                    >
                                        <Plus size={18} />
                                    </button>

                                </div>

                                {/* MOQ NOTE */}

                                <p className="mt-2 text-xs text-gray-500">
                                    Minimum order:
                                    {" "}
                                    {
                                        product?.Product_MOQ
                                    }
                                    {" "}
                                    {
                                        product?.Product_Price_Unit ||
                                        ""
                                    }
                                </p>

                            </div>

                            {/* ==================================================
                                TOTAL PRICE
                            ================================================== */}

                            <div className="mt-6 flex items-center justify-between rounded-2xl bg-white p-5">

                                <div>

                                    <p className="text-sm text-gray-500">
                                        Total
                                    </p>

                                    <p className="mt-1 text-xs text-gray-400">
                                        {quantity}
                                        {" "}
                                        {
                                            product?.Product_Price_Unit ||
                                            "-"
                                        }
                                    </p>

                                </div>

                                <span className="text-xl font-bold text-[#0A2342]">
                                    {formatPrice(
                                        totalPrice,
                                    )}
                                </span>

                            </div>

                            {/* ==================================================
                                ADD TO CART
                            ================================================== */}

                            <div className="mt-6 flex flex-col gap-4 sm:flex-row">

                                <button
                                    type="button"
                                    onClick={() => addcartdata(product, quantity)}
                                    className="flex flex-1 items-center justify-center gap-3 rounded-full bg-[#0A2342] px-7 py-4 font-semibold text-white transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#0A2342]"
                                >
                                    <ShoppingCart size={20} />
                                    Add to Cart
                                </button>

                            </div>

                            {/* ==================================================
                                BACK TO PRODUCTS
                            ================================================== */}

                            <Link
                                href="/products"
                                className="mt-6 inline-flex w-fit items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-[#0A2342]"
                            >
                                <ArrowLeft
                                    size={17}
                                />

                                Back to Products
                            </Link>

                        </div>

                    </div>

                </div>

            </section>

        </main >
    );
}